export const marketingLocales = ["en", "fr"] as const;

export type MarketingLocale = (typeof marketingLocales)[number];

export function isMarketingLocale(value: unknown): value is MarketingLocale {
  return typeof value === "string" && marketingLocales.includes(value as MarketingLocale);
}

export const marketingCopy = {
  en: {
    nav: {
      howItWorks: "How It Works",
      features: "Features",
      benefits: "Benefits",
      pricing: "Pricing",
      organizations: "Organizations",
      createResume: "Create My Resume",
    },
    badge: "100% CANADIAN HOSTING",
    title: "Create a resume that meets Canadian standards",
    description:
      "Upload your current resume. CVBuilder adapts it to Canadian recruiters’ expectations, in English or French, in 4 simple steps.",
    howItWorksCta: "How it works",
    benefits: ["NOC-aligned", "ATS-friendly", "EN and FR"],
    pricingTeaser: {
      freeLabel: "Free version",
      freeDescription: "Build your resume with AI, edit freely, preview with watermark.",
      fullLabel: "Full access — 7 days · $19.99 + tax",
      fullDescription: "HD PDF without watermark, NOC code, English + French resume pack.",
      action: "See details",
    },
    features: {
      eyebrow: "Features",
      title: "Everything you need to stand out to Canadian recruiters",
      items: [
        {
          title: "NOC alignment",
          description:
            "Enter your target job title to instantly get the official NOC code, with no manual search.",
        },
        {
          title: "Canadian resume standards",
          description:
            "Convert your existing resume into a format optimized for local recruiters and applicant tracking systems (ATS).",
        },
        {
          title: "AI-assisted writing",
          description:
            "Generate strong, professional bullet points in seconds, without starting from a blank page.",
        },
        {
          title: "Bilingual support",
          description:
            "Create your resume in English or French, then instantly translate it into the other official language.",
        },
        {
          title: "Data sovereignty",
          description:
            "Platform 100% hosted in Canada. Your personal information stays confidential.",
        },
        {
          title: "No formatting required",
          description:
            "A guided, step-by-step process to quickly build a quality resume. CVBuilder handles the layout.",
        },
      ],
    },
    howItWorks: {
      eyebrow: "How it works",
      title: "Create your resume in 4 simple steps",
      action: "Create My Resume",
      steps: [
        {
          title: "Profile and upload",
          description:
            "Upload an existing resume, enter your basic information and match your target job to its official NOC code.",
        },
        {
          title: "AI analysis",
          description:
            "Our AI analyzes your resume to optimize the content and align it with Canadian standards.",
        },
        {
          title: "Review and refine",
          description:
            "Easily adjust the AI suggestions, refine your details and polish your bullet points.",
        },
        {
          title: "Download",
          description:
            "Instantly get your compliant 2-page PDF resume, ready to send to employers.",
        },
      ],
      note: {
        lead: "You stay in control.",
        rest:
          " Once you upload your resume to the application, CVBuilder generates a resume that meets Canadian standards. You can review and edit it before downloading it in English or French.",
      },
    },
    miniCta: {
      title: "Ready to create your resume?",
      action: "Create My Resume",
    },
    benefitsSection: {
      eyebrow: "Why CVBuilder?",
      title: "A resume tailored to the Canadian job market",
      description:
        "CVBuilder combines artificial intelligence with the real expectations of Canadian recruiters. Get an optimized, impactful resume that fully meets the standards of Canadian employers, in English or French.",
      pillars: [
        {
          title: "Privacy and full control",
          description:
            "Your data is stored 100% in Canada and is never sold. Delete your information at any time, or let our system erase it automatically 7 days after your purchase.",
        },
        {
          title: "Automatic official NOC code",
          description:
            "Simply enter your target job title: CVBuilder identifies and adds the matching NOC code automatically, with no manual search.",
        },
        {
          title: "AI that highlights your true potential",
          description:
            "Generate clear, professional job descriptions without artificial jargon, while keeping your authentic voice.",
        },
        {
          title: "No more formatting headaches",
          description:
            "Focus on your career story. Our platform produces a professional-grade design that meets Canadian standards.",
        },
      ],
    },
    orgBanner: {
      lead: "Are you an organization?",
      rest: " Offer CVBuilder to your clients and advisors, under your own brand.",
      action: "Explore our offer for organizations",
    },
    footer: {
      description: "The smart resume builder for people living in Canada or planning to immigrate.",
      hosting: "Data 100% hosted in Canada.",
      groups: [
        {
          title: "Job Seekers",
          links: ["Create My Resume", "Log in", "Pricing", "Canadian resume standards"],
        },
        {
          title: "Legal",
          links: ["Privacy Policy", "Terms of Service", "Cookie Policy", "Refund Policy"],
        },
      ],
      contact: {
        title: "Contact us",
      },
      copyright: "© 2026 12sc. All rights reserved.",
    },
    mockup: {
      sections: "Sections",
      profile: "Profile",
      achievements: "Achievements",
      experience: "Professional Experience",
      education: "Education & Professional Development",
      additional: "Additional Information",
      complete: "82% complete",
      profileText: "Enthusiastic and creative Florist and Floral Design Student with 3+ years of practical experience in floral arrangement, plant care, event styling, and customer service across Colombia and Canada. Passionate about sustainable floristry, modern European and Nordic floral aesthetics, and creative event design. Reliable, detail-oriented, and fully bilingual in English and French, with native Spanish proficiency.",
      achievementItems: [
        "Reduced floral inventory waste by 15% through proper processing, conditioning, and care of incoming fresh flower shipments.",
        "Created custom floral designs for weddings, corporate events, and private functions in Bogotá.",
        "Developed hands-on expertise in the care and handling of delicate tropical and imported flowers.",
        "Supported customers throughout the purchasing process, from personalized floral orders to safe local delivery preparation.",
      ],
      experienceItems: [
        {
          company: "Elegance Florist",
          role: "Floral Assistant & Florist",
          location: "Montreal, QC, Canada",
          dates: "September 2025 – Present",
          details: [
            "Assemble daily floral bouquets, vase arrangements, and custom orders according to client specifications.",
            "Process, clean, and condition incoming fresh flower shipments to extend vase life and reduce inventory waste by 15%.",
            "Assist retail customers with floral selections and personalized orders.",
            "Process POS transactions and support daily store operations.",
            "Maintain clean, organized, and visually appealing floral displays.",
          ],
        },
        {
          company: "Rosas & Algo Más Florist",
          role: "Junior Florist & Event Assistant",
          location: "Bogotá, Colombia",
          dates: "January 2023 – July 2025",
          details: [
            "Designed custom bouquets and floral centrepieces for weddings, corporate events, and private functions.",
            "Maintained cold storage units and followed hydration and sanitization protocols for delicate tropical and imported flowers.",
            "Assisted with event setup and floral installation.",
            "Responded to customer inquiries through social media and supported order coordination.",
            "Prepared and safely packed floral orders for local courier delivery.",
          ],
        },
      ],
      educationItems: [
        { credential: "Diploma of Vocational Studies (DEP) – Floral Design / Art Floral", institution: "Montreal School of Horticultural Trades, Montreal, QC, Canada", dates: "2025 – Present" },
        { credential: "Technical Certificate in Floristry & Floral Arts", institution: "SENA – National Learning Service, Bogotá, Colombia", dates: "2023" },
      ],
      additionalItems: [
        "Floral Design: Hand-tied bouquets, wedding arrangements, sympathy flowers, centrepieces, colour theory, spiral technique",
        "Botanical Knowledge: Cut flower care, indoor plant maintenance, pest detection, cold-chain handling",
        "Customer service; POS systems; inventory management; event setup and teardown; floral order preparation and delivery",
        "Class 5 equivalent driver's license",
        "Spanish: Native (C2); English: Advanced / Fluent (C1) — IELTS General 7.5; French: Intermediate / Functional (B2) — Quebec French Learning Certificate",
        "Volunteer Florist Assistant, Montreal Botanical Garden — Fall 2025",
      ],
    },
  },
  fr: {
    nav: {
      howItWorks: "Comment ça marche",
      features: "Fonctionnalités",
      benefits: "Avantages",
      pricing: "Tarifs",
      organizations: "Organisations",
      createResume: "Créer mon CV",
    },
    badge: "HÉBERGEMENT 100 % CANADIEN",
    title: "Créez un CV aux normes canadiennes",
    description:
      "Importez votre CV actuel. CVBuilder l’adapte aux attentes des recruteurs canadiens, en français ou en anglais, en 4 étapes simples.",
    howItWorksCta: "Comment ça marche",
    benefits: ["Aligné sur la CNP", "Compatible ATS", "FR et EN"],
    pricingTeaser: {
      freeLabel: "Version gratuite",
      freeDescription: "Créez votre CV avec l’IA, modifiez librement, aperçu avec filigrane.",
      fullLabel: "Accès complet — 7 jours · 19,99 $ + taxes",
      fullDescription: "PDF HD sans filigrane, code CNP, pack CV français + anglais.",
      action: "Voir les détails",
    },
    features: {
      eyebrow: "Fonctionnalités",
      title: "Tout ce qu’il faut pour vous démarquer auprès des recruteurs canadiens",
      items: [
        {
          title: "Alignement CNP",
          description:
            "Saisissez le « titre du poste visé » pour obtenir instantanément le code CNP officiel, sans recherche manuelle.",
        },
        {
          title: "Normes canadiennes du CV",
          description:
            "Convertissez votre CV existant dans un format optimisé pour les attentes des recruteurs locaux et les systèmes de suivi des candidatures (ATS).",
        },
        {
          title: "Rédaction assistée par l’IA",
          description:
            "Générez des puces professionnelles et percutantes en quelques secondes, sans partir de zéro.",
        },
        {
          title: "Soutien bilingue",
          description:
            "Créez votre CV en français ou en anglais, puis traduisez-le instantanément dans l’autre langue officielle.",
        },
        {
          title: "Souveraineté des données",
          description:
            "Plateforme hébergée à 100 % au Canada. Vos renseignements personnels restent confidentiels.",
        },
        {
          title: "Aucune mise en page à faire",
          description:
            "Un parcours guidé étape par étape pour bâtir rapidement un CV de qualité. CVBuilder s’occupe de la mise en page.",
        },
      ],
    },
    howItWorks: {
      eyebrow: "Comment ça marche",
      title: "Créez votre CV en 4 étapes simples",
      action: "Créer mon CV",
      steps: [
        {
          title: "Profil et importation",
          description:
            "Importez un CV existant, saisissez vos informations de base et associez le code CNP officiel au poste visé.",
        },
        {
          title: "Analyse par l’IA",
          description:
            "Notre IA analyse votre CV pour optimiser le contenu et l’aligner sur les normes canadiennes.",
        },
        {
          title: "Révision et ajustement",
          description:
            "Ajustez facilement les suggestions de l’IA, affinez vos détails et peaufinez vos puces.",
        },
        {
          title: "Téléchargement",
          description:
            "Obtenez immédiatement votre CV PDF de 2 pages, conforme et prêt à être envoyé aux employeurs.",
        },
      ],
      note: {
        lead: "Vous gardez le contrôle.",
        rest:
          " Une fois votre CV téléversé dans l’application, CVBuilder génère un CV aux normes canadiennes. Vous pouvez l’examiner et le modifier avant de le télécharger en français ou en anglais.",
      },
    },
    miniCta: {
      title: "Prêt à créer votre CV?",
      action: "Créer mon CV",
    },
    benefitsSection: {
      eyebrow: "Pourquoi CVBuilder?",
      title: "Un CV conçu sur mesure pour le marché du travail canadien",
      description:
        "CVBuilder associe l’intelligence artificielle aux exigences réelles des recruteurs d’ici. Obtenez un CV optimisé, percutant et parfaitement adapté aux standards des entreprises canadiennes, en français comme en anglais.",
      pillars: [
        {
          title: "Confidentialité et contrôle total",
          description:
            "Vos données sont stockées à 100 % au Canada et ne sont jamais revendues. Supprimez vos informations à tout moment, ou laissez notre système les effacer automatiquement 7 jours après votre achat.",
        },
        {
          title: "Code CNP officiel automatique",
          description:
            "Entrez simplement l’intitulé de votre poste visé : CVBuilder identifie et ajoute automatiquement le code CNP correspondant, sans aucune recherche manuelle.",
        },
        {
          title: "Une IA qui valorise votre vrai potentiel",
          description:
            "Générez des descriptions de postes claires et professionnelles sans jargon artificiel, tout en conservant votre authenticité.",
        },
        {
          title: "Fini le cauchemar de la mise en page",
          description:
            "Concentrez-vous sur votre parcours, notre plateforme génère un design de calibre professionnel qui respecte les standards d’ici.",
        },
      ],
    },
    orgBanner: {
      lead: "Vous êtes une organisation?",
      rest: " Offrez CVBuilder à vos candidats et à vos conseillers, à vos couleurs.",
      action: "Découvrir l’offre organisations",
    },
    footer: {
      description: "Le générateur de CV intelligent pour les personnes qui habitent au Canada ou qui souhaitent y immigrer.",
      hosting: "Données hébergées à 100 % au Canada.",
      groups: [
        {
          title: "Candidats",
          links: ["Créer mon CV", "Connexion", "Tarifs", "Normes canadiennes du CV"],
        },
        {
          title: "Légal",
          links: [
            "Politique de confidentialité",
            "Conditions d’utilisation",
            "Politique de gestion des témoins",
            "Politique de remboursement",
          ],
        },
      ],
      contact: {
        title: "Contactez-nous",
      },
      copyright: "© 2026 12sc. Tous droits réservés.",
    },
    mockup: {
      sections: "Sections",
      profile: "Profil",
      achievements: "Réalisations",
      experience: "Expérience professionnelle",
      education: "Formation et perfectionnement professionnel",
      additional: "Informations supplémentaires",
      complete: "82 % terminé",
      profileText: "Créative, rigoureuse et passionnée, je suis une fleuriste et étudiante en art floral cumulant plus de 3 ans d'expérience pratique en Colombie et au Canada. Spécialisée en entretien des plantes, composition florale, service à la clientèle et stylisme événementiel, je m'inspire grandement de l'esthétique moderne européenne et nordique tout en promouvant des pratiques florales durables. Parfaitement trilingue (anglais, français et espagnol), je fais preuve d'une excellente fiabilité dans la réalisation de projets événementiels sur mesure.",
      achievementItems: [
        "Optimisation du soin, de la préparation et du conditionnement des arrivages de fleurs fraîches, permettant de diminuer le gaspillage floral de 15 %.",
        "Conception d'arrangements floraux personnalisés destinés aux réceptions privées, mariages et événements corporatifs à Bogota.",
        "Prise en charge complète de la clientèle, depuis la prise de commande sur mesure jusqu'à l'organisation sécurisée des livraisons locales.",
        "Savoir-faire éprouvé dans la manipulation ainsi que le suivi méticuleux des variétés florales délicates, tropicales ou importées.",
      ],
      experienceItems: [
        {
          company: "Fleuriste Élégance",
          role: "Assistant(e) floral(e) et fleuriste",
          location: "Montréal, QC, Canada",
          dates: "Septembre 2025 – Aujourd'hui",
          details: [
            "Création sur mesure de bouquets et d'arrangements floraux adaptés aux besoins spécifiques de chaque client.",
            "Nettoyage, traitement et préparation rigoureux des fleurs fraîches pour la livraison, prolongeant la tenue en vase et réduisant les pertes de 15 %.",
            "Accompagnement et conseil personnalisé dans la sélection des variétés florales.",
            "Gestion des encaissements et contribution active au bon déroulement des activités en boutique.",
            "Entretien et mise en valeur des présentations florales en magasin pour garantir un espace accueillant et soigné.",
          ],
        },
        {
          company: "Rosas & Algo Más Florist",
          role: "Fleuriste Junior & Assistante Événementielle",
          location: "Bogotá, Colombie",
          dates: "Janvier 2023 – Juillet 2025",
          details: [
            "Création de bouquets et de centres de table floraux sur mesure pour mariages, événements corporatifs et réceptions privées.",
            "Gestion des chambres froides et respect des protocoles d'hydratation et de désinfection pour les fleurs tropicales et importées délicates.",
            "Aide à la mise en place des événements et à l'installation florale.",
            "Réponse aux demandes des clients via les réseaux sociaux et soutien à la coordination des commandes.",
            "Préparation et emballage sécurisé des commandes florales pour livraison par coursier local.",
          ],
        },
      ],
      educationItems: [
        { credential: "Diplôme d'études professionnelles (DEP) – Design floral / Art floral", institution: "École des métiers horticoles de Montréal, Montréal (Québec), Canada", dates: "2025 – Aujourd'hui" },
        { credential: "Certificat technique en art floral", institution: "SENA – Service national d'apprentissage, Bogota, Colombie", dates: "2023" },
      ],
      additionalItems: [
        "Design & art floral : Création de bouquets champêtres, compositions pour mariages, fleurs de deuil et centres de table; maîtrise de la théorie des couleurs et de la technique de la spirale",
        "Horticulture & botanique : Soin des fleurs coupées, entretien des plantes d’intérieur, détection des parasites et manipulation à froid",
        "Gestion & opérations : Service à la clientèle, systèmes de point de vente, gestion des stocks, préparation et livraison des commandes de fleurs",
        "Événementiel : Montage, installation et démontage de décors floraux",
        "Compétences linguistiques : Espagnol (langue maternelle, C2), Anglais (courant/avancé, C1 – IELTS Général 7,5), Français (intermédiaire/fonctionnel, B2)",
        "Engagement communautaire : Assistant fleuriste bénévole au Jardin botanique de Montréal (Automne 2025); Permis de conduire : Classe 5 ou équivalent",
      ],
    },
  },
} as const satisfies Record<MarketingLocale, object>;
